import { Fragment } from 'react'
import { createFileRoute, Link as RouterLink, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { Box, Button, Card, Flex, Heading, Link, Table, Text } from '@radix-ui/themes'
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { CellText } from '~/components/CellText'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'
import { formatTimestamp } from '~/utils/date'
import { formatM3, formatPercent, gapPercent, isScaleError } from '~/features/index-captures/alignment'
import { CaptureStatusBadge, ScaleErrorBadge } from '~/features/index-captures/components/CaptureBadges'
import { CaptureDetailPanel } from '~/features/index-captures/components/CaptureDetailPanel'
import { roundLabel } from '~/features/index-captures/labels'

type CaptureRow = TRPCOutputs['ops']['indexCaptures']['listQueue'][number]

interface IndexCapturesSearch {
  capture?: string
}

interface ExpansionProps {
  expandedId: string | undefined
  onToggle: (captureId: string) => void
}

const COLUMN_COUNT = 8

const StatusCell = ({ row }: { row: CaptureRow }) => (
  <Flex align="center" gap="2" wrap="wrap">
    <CaptureStatusBadge status={row.status} outcome={row.outcome} />
    {isScaleError(row) ? <ScaleErrorBadge /> : null}
  </Flex>
)

const Gap = ({ row }: { row: CaptureRow }) => (
  <Flex direction="column">
    <Text size="2">{formatPercent(gapPercent(row))}</Text>
    <Text size="1" color="gray">
      {formatM3(row.delta)}
    </Text>
  </Flex>
)

const InstallationCell = ({ row }: { row: CaptureRow }) => (
  <Flex direction="column">
    <Link asChild size="1" className="font-mono">
      <RouterLink
        to="/installations/$installationId"
        params={{ installationId: String(row.installationId) }}
        onClick={(event) => event.stopPropagation()}
      >
        #{row.installationId}
      </RouterLink>
    </Link>
    <Text size="2">{row.name ?? '—'}</Text>
  </Flex>
)

const CaptureTableRow = ({ row, expandedId, onToggle }: ExpansionProps & { row: CaptureRow }) => {
  const isOpen = expandedId === row.id
  return (
    <Fragment>
      <Table.Row onClick={() => onToggle(row.id)} className="cursor-pointer hover:bg-[var(--gray-a2)]">
        <Table.Cell>{isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}</Table.Cell>
        <Table.Cell>
          <StatusCell row={row} />
        </Table.Cell>
        <Table.Cell>
          <InstallationCell row={row} />
        </Table.Cell>
        <Table.Cell>
          <CellText value={row.orgName} />
        </Table.Cell>
        <Table.Cell>
          <CellText value={row.siteName} />
        </Table.Cell>
        <Table.Cell>
          <CellText value={roundLabel(row.round)} />
        </Table.Cell>
        <Table.Cell>
          <Gap row={row} />
        </Table.Cell>
        <Table.Cell>
          <Text size="2">{formatTimestamp(row.requestedAt)}</Text>
        </Table.Cell>
      </Table.Row>
      {isOpen ? (
        <Table.Row>
          <Table.Cell colSpan={COLUMN_COUNT}>
            <Box p="2">
              <CaptureDetailPanel captureId={row.id} />
            </Box>
          </Table.Cell>
        </Table.Row>
      ) : null}
    </Fragment>
  )
}

/** A linked capture that is not in the table, e.g. a scheduled round that matched and left the queue. */
const LinkedCapture = ({ expandedId, onToggle, rows }: ExpansionProps & { rows: CaptureRow[] }) =>
  expandedId && !rows.some((row) => row.id === expandedId) ? (
    <Card>
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between">
          <Heading size="3">Linked capture</Heading>
          <Button size="1" variant="ghost" onClick={() => onToggle(expandedId)}>
            Close
          </Button>
        </Flex>
        <CaptureDetailPanel key={expandedId} captureId={expandedId} />
      </Flex>
    </Card>
  ) : null

const IndexCapturesPage = () => {
  const trpc = useTRPC()
  const navigate = useNavigate({ from: Route.fullPath })
  const { capture } = Route.useSearch()
  const { data: rows = [], isLoading } = useQuery(trpc.ops.indexCaptures.listQueue.queryOptions())

  const toggleCapture = (captureId: string) =>
    void navigate({ search: capture === captureId ? {} : { capture: captureId }, replace: true })

  return (
    <Flex direction="column" gap="4">
      <Heading size="5">Index captures</Heading>
      <Text size="2" color="gray">
        Rounds that did not come back clean, then the captures requested by hand. Resolving a round is what starts
        the next one&apos;s clock.
      </Text>

      {isLoading ? null : <LinkedCapture expandedId={capture} onToggle={toggleCapture} rows={rows} />}

      {isLoading ? (
        <Text color="gray">Loading…</Text>
      ) : rows.length === 0 ? (
        <Box py="6">
          <Text color="gray">Nothing to work — every capture matched or is still in flight.</Text>
        </Box>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell />
              <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Installation</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Organization</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Site</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Round</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Gap</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Requested</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((row) => (
              <CaptureTableRow key={row.id} row={row} expandedId={capture} onToggle={toggleCapture} />
            ))}
          </Table.Body>
        </Table.Root>
      )}
    </Flex>
  )
}

export const Route = createFileRoute('/index-captures/')({
  component: IndexCapturesPage,
  validateSearch: (raw: Record<string, unknown>): IndexCapturesSearch => ({
    capture: z.string().uuid().safeParse(raw.capture).data
  })
})
